import React, { useCallback, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import usePermissions from "hooks/usePermissions";
import { useStore } from "stores/connect";
import { Button } from "components/bootstrap";
import BootstrapModalForm from "components/bootstrap/BootstrapModalForm";
import UrlAllowListForm from "components/configurations/UrlAllowListForm";
import { ConfigurationsActions, ConfigurationsStore } from "stores/configurations/ConfigurationsStore";
import IfPermitted from "components/common/IfPermitted";
import generateId from "logic/generateId";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
const URL_ALLOWLIST_CONFIG = "org.graylog2.system.urlallowlist.UrlAllowlist";
const URLAllowListFormModal = ({ newUrlEntry = "", urlType = void 0, onUpdate = () => {
} }) => {
  const { t } = useTranslation("common");
  const prevNewUrlEntry = useRef();
  const [config, setConfig] = useState({ entries: [], disabled: false });
  const [isValid, setIsValid] = useState(false);
  const [newUrlEntryId, setNewUrlEntryId] = useState();
  const [showConfigModal, setShowConfigModal] = useState(false);
  const { configuration } = useStore(ConfigurationsStore);
  const urlAllowListConfig = configuration[URL_ALLOWLIST_CONFIG];
  const { isPermitted } = usePermissions();
  const sendTelemetry = useSendTelemetry("urlallowlist");
  useEffect(() => {
    if (isPermitted(["urlallowlist:read"])) {
      ConfigurationsActions.listAllowListConfig(URL_ALLOWLIST_CONFIG);
    }
  }, [isPermitted]);
  const setDefaultAllowListState = useCallback(
    (defaultUrlAllowListConfig) => {
      const id = generateId();
      const defaultConfig = {
        entries: [
          ...defaultUrlAllowListConfig.entries,
          {
            id,
            title: "",
            value: newUrlEntry,
            type: urlType != null ? urlType : "literal"
          }
        ],
        disabled: defaultUrlAllowListConfig.disabled
      };
      setNewUrlEntryId(id);
      setConfig(defaultConfig);
    },
    [newUrlEntry, urlType]
  );
  useEffect(() => {
    const { entries } = config;
    if (urlAllowListConfig) {
      if (entries.length === 0 || prevNewUrlEntry.current !== newUrlEntry) {
        setDefaultAllowListState(urlAllowListConfig);
      }
    }
    prevNewUrlEntry.current = newUrlEntry;
  }, [setDefaultAllowListState, urlAllowListConfig, config, newUrlEntry, urlType]);
  const openModal = () => {
    setShowConfigModal(true);
  };
  const closeModal = () => {
    setShowConfigModal(false);
    setDefaultAllowListState(urlAllowListConfig);
  };
  const handleUpdate = (nextConfig, nextIsValid) => {
    setConfig(nextConfig);
    setIsValid(nextIsValid);
  };
  const saveConfig = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    sendTelemetry(TELEMETRY_EVENT_TYPE.URLALLOWLIST_CONFIGURATION_UPDATED, {
      app_action_value: "configuration-update"
    });
    if (isValid) {
      ConfigurationsActions.updateAllowlist(URL_ALLOWLIST_CONFIG, config).then(() => {
        onUpdate();
        closeModal();
      });
    }
  };
  if (urlAllowListConfig) {
    const { entries, disabled } = config;
    return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "urlallowlist:write" }, /* @__PURE__ */ React.createElement(Button, { bsStyle: "info", bsSize: "xs", onClick: openModal }, t("url_allow_list_modal.add"))), /* @__PURE__ */ React.createElement(
      BootstrapModalForm,
      {
        show: showConfigModal,
        bsSize: "lg",
        title: t("url_allow_list_modal.title"),
        onCancel: closeModal,
        onSubmitForm: saveConfig,
        submitButtonDisabled: !isValid,
        submitButtonText: t("url_allow_list_modal.submit")
      },
      /* @__PURE__ */ React.createElement("h3", null, t("url_allow_list_modal.urls")),
      /* @__PURE__ */ React.createElement(
        UrlAllowListForm,
        {
          key: newUrlEntryId,
          urls: entries,
          disabled,
          onUpdate: handleUpdate,
          newEntryId: newUrlEntryId
        }
      )
    ));
  }
  return null;
};
export default URLAllowListFormModal;
